import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Constructor } from '../mixins/constructor';
import { AgencyBlocAgent } from './agency-bloc-agent';
import { AgencyBlocGroup } from './agency-bloc-group';
import { AgencyBlocIndividual } from './agency-bloc-individual';
import { AgencyBlocPolicy } from './agency-bloc-policy';
import { HasEmailAttribute, HasFullNameAttribute } from './search-results-record';

/**
 * The AgencyBloc entity types
 */
export const AgencyBlocEnties = ['Agent', 'Individual', 'Group', 'Policy'];

/**
 * The AgencyBloc entity types
 */
export type AgencyBlocEntity = AgencyBlocAgent | AgencyBlocIndividual | AgencyBlocGroup | AgencyBlocPolicy;

/**
 * A mixin to add AgencyBloc entity properties to a class
 */
export function AgencyBlocEntity<T extends Constructor<{}>>(Base: T = class {} as any) {
  return class extends Base {
    /**
     * The property used by AgencyBloc for unique records
     */
    public $entityID?: string;

    /**
     * A string representation of the entity type
     */
    public $entityType?: string;

    /**
     * The label to use when displaying records to the user
     */
    public $entityDateLabel?: string;

    /**
     * The date value to use when displaying records to the user
     */
    public $entityDate?: Date;

    /**
     * The AgencyBloc ID for unique records
     */
    public $entityId?: number;

    /**
     * Is attention required for the record?
     */
    public attn_required = false;

    constructor(...args: any[]) {
      super(...args);
    }

    public createFormGroup(): FormGroup {
      return new FormGroup({
        entityType: new FormControl(this.$entityType, [Validators.required]),
        entityID: new FormControl(this.$entityID, [Validators.required]),
        entityId: new FormControl(this.$entityId, [Validators.required]),
      });
    }
  };
}

export class GenericAgencyBlocEntity extends AgencyBlocEntity(HasEmailAttribute(HasFullNameAttribute())) {
  /**
   * Is this record checked in the UI?
   */
  public checked = false;

  /**
   * The underlying AgencyBloc entity
   */
  public resolved: AgencyBlocEntity = new AgencyBlocIndividual();

  /**
   * Get the entity date for this Model. Usually used for sorting and displaying in the UI.
   */
  public get entityDate(): Date {
    return this.resolved.entityDate;
  }

  /**
   * Get the entity date label for this Model. Usually used for displaying in the UI.
   */
  public get entityDateLabel(): string {
    return this.resolved.$entityDateLabel;
  }

  /**
   * Get the entity ID for this Model. Usually used for creating or syncing records.
   */
  public get entityId(): number {
    return this.resolved.entityId;
  }

  /**
   * Get the entity type for the Model. Usually used to create the correct resolved object
   */
  public get entityType(): string {
    if (this.hasOwnProperty('agentID')) return 'Agent';
    if (this.hasOwnProperty('individualID')) return 'Individual';
    if (this.hasOwnProperty('groupID')) return 'Group';
    return 'Policy';
  }

  /**
   * Get the AgencyBloc ID property name for this Model.
   */
  public get entityID(): string {
    if (this.hasOwnProperty('agentID')) return 'agentID';
    if (this.hasOwnProperty('individualID')) return 'individualID';
    if (this.hasOwnProperty('groupID')) return 'groupID';
    return 'PolicyID';
  }

  /**
   * If entity type is AgencyblocGroup
   */
  public groupName?:string;

  constructor(record: Partial<AgencyBlocEntity>) {
    super(record);
    Object.assign(this, record);

    if (record) {
      if (this.entityType === 'Agent') {
        this.resolved = new AgencyBlocAgent(record as unknown as AgencyBlocAgent);
      } else if (this.entityType === 'Individual') {
        this.resolved = new AgencyBlocIndividual(record as unknown as AgencyBlocIndividual);
      } else if (this.entityType === 'Group') {
        this.resolved = new AgencyBlocGroup(record as unknown as AgencyBlocGroup);
      } else {
        this.resolved = new AgencyBlocPolicy(record as unknown as AgencyBlocPolicy);
      }
    }
  }

  public createFormGroup(): FormGroup {
    return this.resolved.createFormGroup();
  }
}
